import { Tournament, TournamentType } from '../models';
import { LeagueRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { ModerateContentUseCase } from './ModerateContentUseCase';

export class CreateLeagueTournamentUseCase {
  constructor(
    private leagueRepository: LeagueRepository,
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private moderateContent: ModerateContentUseCase,
  ) {}

  async execute(params: {
    leagueId: string;
    name: string;
    tournamentType: TournamentType;
    userId: string;
  }): Promise<Tournament> {
    const { leagueId, name, tournamentType, userId } = params;

    const league = await this.leagueRepository.getById(leagueId);
    if (!league) {
      throw new Error('League not found');
    }

    if (league.userId !== userId) {
      throw new Error('Only the league owner can create tournaments');
    }

    if (league.status === 'completed') {
      throw new Error('Cannot add tournaments to a completed league');
    }

    // Get league participants
    const leagueParticipants = await this.leagueRepository.getParticipants(leagueId);

    if (leagueParticipants.length < 2) {
      throw new Error('League must have at least 2 participants');
    }

    // Moderate content before writing
    await this.moderateContent.execute(name, 'tournament_name', userId);

    // Create tournament with leagueId
    const tournament = await this.tournamentRepository.create({
      name: name.trim(),
      tournamentType,
      status: 'setup',
      maxParticipants: leagueParticipants.length,
      userId,
      isPublic: league.isPublic ?? false,
      leagueId,
    });

    // Copy league participants to tournament
    for (let i = 0; i < leagueParticipants.length; i++) {
      const lp = leagueParticipants[i];
      await this.participantRepository.create({
        tournamentId: tournament.id,
        name: lp.name,
        seed: i + 1,
        userId: lp.userId,
      });
    }

    // Transition league from setup to active if this is the first tournament
    if (league.status === 'setup') {
      await this.leagueRepository.update(leagueId, { status: 'active' });
    }

    return tournament;
  }
}
